import type { QueryClient } from '@tanstack/react-query';
import { orgScope, queryKeys } from '@/lib/query-keys';
import { orgPluginsQueryKey } from './useOrgPlugins';
import { orgPluginQueryKey } from './useOrgPlugin';
import { orgPluginUninstallPreviewQueryKey } from './useOrgPluginUninstallPreview';

/**
 * Refresh every React Query slot that depends on a per-org plugin
 * installation transition (`installing` → `installed`, uninstall, cascade).
 *
 * Callers:
 * - `useOrgPluginInstallationSocket` on every `org:plugin_installation_changed`
 * - Install / uninstall mutation `onSuccess` handlers (HTTP returns only after
 *   the row reaches a terminal state), so nav/routes update even when the
 *   socket listener is not mounted on the current page
 *
 * The plugin-manifest slot (`queryKeys.plugins`) is the critical one for
 * sidebar / `/integrations` freshness: the server strips `routes`/`navItems`
 * from uninstalled manifests (`filterManifestForActiveOrg`), and
 * `usePlugins` caches that response with `staleTime: Infinity`.
 */
export function invalidateOrgPluginInstallationQueries(
  queryClient: QueryClient,
  organizationId: string,
  pluginNames: readonly string[],
): void {
  void queryClient.invalidateQueries({
    queryKey: orgPluginsQueryKey(organizationId),
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.plugins(orgScope(organizationId)),
  });
  for (const name of pluginNames) {
    void queryClient.invalidateQueries({
      queryKey: orgPluginQueryKey(organizationId, name),
    });
    void queryClient.invalidateQueries({
      queryKey: orgPluginUninstallPreviewQueryKey(organizationId, name),
    });
  }
}
